import { type FC, type SetStateAction, type Dispatch, useCallback, useState } from 'react';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';
import { FormattedMessage, defineMessages, useIntl } from 'dibs-react-intl';
import CameraIcon from 'dibs-icons/exports/legacy/Camera.ts';

import { cropAndResizeImage, type ProcessImagePayload } from '../../services/cropService.ts';
import { saveProfile } from '../../services/profileService.ts';
import {
    isImageValidDims,
    uploadImage,
    type UploadImagePayload,
} from '../../services/mediaService.ts';
import { InlineMediaUploader } from './InlineMediaUploader.tsx';
import { MediaErrorBanner } from './MediaErrorBanner.tsx';
import * as constants from '../../constants.ts';
import { type NftPublicProfileEditableMedia_publicProfile$key } from './__generated__/NftPublicProfileEditableMedia_publicProfile.graphql.ts';

import styles from './NftPublicProfileEditableMedia.scss';

const CoverImagePlaceholder: FC = () => {
    return (
        <div className={styles.coverPlacholder}>
            <div className={styles.placeholderText}>
                <CameraIcon className={styles.cameraIcon} />
                <FormattedMessage
                    id="nft.publicProfile.mediaUploader.coverPlaceholder"
                    defaultMessage="Add banner image"
                />
            </div>
        </div>
    );
};

const AvatarImagePlaceholder: FC = () => {
    return (
        <div className={styles.avatarPlaceholder}>
            <div className={styles.placeholderText}>
                <CameraIcon className={styles.cameraIcon} />
                <FormattedMessage
                    id="nft.publicProfile.mediaUploader.avatarPlaceholder"
                    defaultMessage="Add avatar image"
                />
            </div>
        </div>
    );
};

type Props = {
    publicProfile: NftPublicProfileEditableMedia_publicProfile$key | null | undefined;
};

// either avatarImageId or coverImageId. Can't be both
type SingleImageOptionInput =
    | { avatarImageId: number | string | null; coverImageId?: undefined }
    | { avatarImageId?: undefined; coverImageId: number | string | null };

type StateSetter<T> = Dispatch<SetStateAction<T>>;

type MediaToCrop = {
    path: string;
    id: string | number;
} | null;

type CropAndResizeArgs = ProcessImagePayload & { photoId: number };

const messages = defineMessages({
    errorUploadingMedia: {
        id: 'nft.publicProfile.mediaUploader.errorUploadingMedia',
        defaultMessage: 'Unexpected error while uploading media.',
    },
    errorSavingProfile: {
        id: 'nft.publicProfile.mediaUploader.errorSavingProfile',
        defaultMessage: 'Unexpected error while updating your profile.',
    },
});

export const NftPublicProfileEditableMedia: FC<Props> = ({ publicProfile: publicProfileRef }) => {
    const environment = useRelayEnvironment();
    const intl = useIntl();
    const publicProfile = useFragment(
        graphql`
            fragment NftPublicProfileEditableMedia_publicProfile on PublicProfile {
                userId
                username
                avatarImage {
                    serviceId
                    path
                    parent {
                        serviceId
                    }
                }
                heroImage {
                    serviceId
                    path
                    parent {
                        serviceId
                    }
                }
                ...NftPublicProfileEditForm_publicProfile @relay(mask: false)
            }
        `,
        publicProfileRef
    );
    const { username, userId, avatarImage, heroImage } = publicProfile ?? {};
    const avatarPath = avatarImage?.path;
    const coverPath = heroImage?.path;

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [coverMediaToCrop, setCoverMediaToCrop] = useState<MediaToCrop>(null);
    const [avatarMediaToCrop, setAvatarMediaToCrop] = useState<MediaToCrop>(null);

    const saveImageToProfile = useCallback(
        async ({ avatarImageId, coverImageId }: SingleImageOptionInput) => {
            if (userId && username) {
                let input;

                if (typeof avatarImageId !== 'undefined') {
                    input = {
                        ...publicProfile,
                        username,
                        userId,
                        heroImageId: publicProfile?.heroImage?.serviceId,
                        avatarImageId,
                    };
                } else {
                    input = {
                        ...publicProfile,
                        username,
                        userId,
                        heroImageId: coverImageId,
                        avatarImageId: publicProfile?.avatarImage?.serviceId,
                    };
                }

                try {
                    await saveProfile({
                        input,
                        environment,
                    });
                } catch {
                    setErrorMessage(intl.formatMessage(messages.errorSavingProfile));
                }
            }
        },
        [publicProfile, username, environment, userId, intl]
    );

    const uploadMedia = useCallback(
        async (
            files: Array<File>,
            targetHeight: number,
            targetWidth: number,
            photoType: UploadImagePayload['photoType'],
            setMediaToCrop: StateSetter<MediaToCrop>,
            saveToProfile: (mediaId: string | number) => Promise<void>
        ): Promise<void> => {
            if (!userId) {
                return;
            }

            const { error } = await isImageValidDims({
                files,
                minHeight: targetHeight,
                minWidth: targetWidth,
            });

            if (error) {
                setErrorMessage(error);
                return;
            }

            const { mediaInfo } = await uploadImage({
                userId,
                files,
                photoType,
            });

            if (!mediaInfo) {
                setErrorMessage(intl.formatMessage(messages.errorUploadingMedia));
                return;
            }

            const needsCropping =
                mediaInfo.dimension?.height !== targetHeight ||
                mediaInfo.dimension?.width !== targetWidth;

            if (needsCropping) {
                setMediaToCrop({
                    path: mediaInfo.mediaLocation ?? '',
                    id: mediaInfo.mediaId,
                });
            } else {
                await saveToProfile(mediaInfo.mediaId);
            }
        },
        [userId, intl]
    );

    const saveMediaAfterCropping = useCallback(
        async (
            cropPayload: CropAndResizeArgs,
            setMediaToCrop: StateSetter<MediaToCrop>,
            saveToProfile: (mediaId: string | number) => Promise<void>
        ): Promise<void> => {
            const { photoId, resize, crop } = cropPayload ?? {};

            if (photoId && resize) {
                setMediaToCrop(null);

                const { mediaInfo } = await cropAndResizeImage({
                    id: photoId,
                    data: { resize, crop },
                });

                if (mediaInfo?.mediaId) {
                    await saveToProfile(mediaInfo?.mediaId);
                }
            }
        },
        []
    );

    const onCoverPhotoDelete = useCallback(async (): Promise<void> => {
        await saveImageToProfile({ coverImageId: null });
    }, [saveImageToProfile]);

    const onAvatarPhotoDelete = useCallback(async (): Promise<void> => {
        await saveImageToProfile({ avatarImageId: null });
    }, [saveImageToProfile]);

    const onCoverPhotoUpload = useCallback(
        async (files: Array<File>) => {
            const { targetHeight, targetWidth } = constants.coverImageUploaderReqs;
            await uploadMedia(
                files,
                targetHeight,
                targetWidth,
                'PUBLIC_PROFILE_COVER',
                setCoverMediaToCrop,
                mediaId => saveImageToProfile({ coverImageId: mediaId })
            );
        },
        [uploadMedia, saveImageToProfile]
    );

    const onAvatarPhotoUpload = useCallback(
        async (files: Array<File>) => {
            const { targetHeight, targetWidth } = constants.avatarImageUploaderReqs;
            await uploadMedia(
                files,
                targetHeight,
                targetWidth,
                'PUBLIC_PROFILE_AVATAR',
                setAvatarMediaToCrop,
                mediaId => saveImageToProfile({ avatarImageId: mediaId })
            );
        },
        [uploadMedia, saveImageToProfile]
    );

    const onCoverPhotoCrop = useCallback(() => {
        const coverId = heroImage?.parent?.serviceId ?? heroImage?.serviceId;
        if (coverId && coverPath) {
            setCoverMediaToCrop({
                path: coverPath,
                id: coverId,
            });
        }
    }, [heroImage, coverPath]);

    const onAvatarPhotoCrop = useCallback(() => {
        const avatarId = avatarImage?.parent?.serviceId ?? avatarImage?.serviceId;
        if (avatarId && avatarPath) {
            setAvatarMediaToCrop({
                path: avatarPath,
                id: avatarId,
            });
        }
    }, [avatarImage, avatarPath]);

    const onCoverPhotoCropComplete = useCallback(
        async (cropPayload: CropAndResizeArgs) => {
            await saveMediaAfterCropping(cropPayload, setCoverMediaToCrop, mediaId =>
                saveImageToProfile({ coverImageId: mediaId })
            );
        },
        [saveImageToProfile, saveMediaAfterCropping]
    );

    const onAvatarPhotoCropComplete = useCallback(
        async (cropPayload: CropAndResizeArgs) => {
            await saveMediaAfterCropping(cropPayload, setAvatarMediaToCrop, mediaId =>
                saveImageToProfile({ avatarImageId: mediaId })
            );
        },
        [saveImageToProfile, saveMediaAfterCropping]
    );

    return (
        <>
            <InlineMediaUploader
                src={coverPath}
                alt={`${username}-cover`}
                imageClass={styles.coverImage}
                containerClass={styles.coverContainer}
                onModalClose={() => setCoverMediaToCrop(null)}
                onDrop={onCoverPhotoUpload}
                onDelete={onCoverPhotoDelete}
                onCrop={onCoverPhotoCrop}
                onCropComplete={onCoverPhotoCropComplete}
                placeholder={<CoverImagePlaceholder />}
                imageToCrop={coverMediaToCrop}
                imageRequirements={{
                    cropShape: 'rect',
                    targetHeight: constants.coverImageUploaderReqs.targetHeight,
                    targetWidth: constants.coverImageUploaderReqs.targetWidth,
                }}
            />

            <InlineMediaUploader
                src={avatarPath}
                alt={username ?? ''}
                imageClass={styles.avatarImage}
                containerClass={styles.avatarContainer}
                onModalClose={() => setAvatarMediaToCrop(null)}
                onDrop={onAvatarPhotoUpload}
                onDelete={onAvatarPhotoDelete}
                onCrop={onAvatarPhotoCrop}
                onCropComplete={onAvatarPhotoCropComplete}
                placeholder={<AvatarImagePlaceholder />}
                imageToCrop={avatarMediaToCrop}
                isRoundImage
                imageRequirements={{
                    cropShape: 'round',
                    targetHeight: constants.avatarImageUploaderReqs.targetHeight,
                    targetWidth: constants.avatarImageUploaderReqs.targetWidth,
                }}
            />

            <MediaErrorBanner message={errorMessage} clearMessage={() => setErrorMessage(null)} />
        </>
    );
};
